import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminAccessDenied } from "@/components/admin/AdminAccessDenied";
import { AdminLoading, useAdminAuth } from "@/components/admin/useAdminAuth";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminJobReview() {
    const { user, logout, hasPermission } = useAdminAuth();
    const [jobs, setJobs] = useState(null);
    const [busy, setBusy] = useState("");

    const load = () => fetch(`${API}/api/admin/employer-jobs/pending`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then(setJobs);

    useEffect(() => {
        if (user) load();
    }, [user]);

    const review = async (job, decision) => {
        const notes = decision === "reject" ? window.prompt("Reason for rejection (shown to the employer):") || "" : "";
        if (decision === "reject" && !notes.trim()) return;
        setBusy(job.id);
        await fetch(`${API}/api/admin/employer-jobs/${job.id}/review`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ decision, notes }),
        });
        setBusy("");
        load();
    };

    if (!user) return <AdminLoading />;
    if (!hasPermission("employers")) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] text-ink">
                <AdminNav active="review" email={user.email} user={user} onLogout={logout} />
                <AdminAccessDenied section="employer job review" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ivory text-ink">
            <AdminNav active="review" email={user.email} user={user} onLogout={logout} />
            <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <h1 className="font-serif text-3xl font-medium tracking-tight">Job Review</h1>
                <p className="mt-1 text-sm text-ink/55">Employer vacancies waiting for Afrinexa moderation before publication.</p>
                {!jobs ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-forest" /></div> : jobs.length === 0 ? (
                    <p data-testid="job-review-empty" className="mt-10 border-t border-ink/10 py-16 text-center text-sm text-ink/50">No employer jobs are pending review.</p>
                ) : (
                    <div className="mt-10 space-y-5" data-testid="job-review-list">
                        {jobs.map((job) => (
                            <article key={job.id} data-testid={`review-job-${job.id}`} className="border border-ink/10 bg-ivory p-6">
                                <div className="flex flex-wrap items-start justify-between gap-5">
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-champagne">Pending review</p>
                                        <h2 className="mt-2 font-serif text-2xl font-medium">{job.title}</h2>
                                        <p className="mt-1 text-sm text-ink/55">{job.company} · {job.city ? `${job.city}, ` : ""}{job.country} · {job.vacancies || job.positions} vacancies</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button type="button" data-testid={`review-publish-${job.id}`} disabled={busy === job.id} onClick={() => review(job, "publish")} className="inline-flex items-center gap-2 bg-forest px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ivory hover:bg-forest-deep disabled:opacity-60"><CheckCircle2 size={14} /> Publish</button>
                                        <button type="button" data-testid={`review-reject-${job.id}`} disabled={busy === job.id} onClick={() => review(job, "reject")} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink/60 hover:border-red-900/40 hover:text-red-900 disabled:opacity-60"><XCircle size={14} /> Reject</button>
                                    </div>
                                </div>
                                {job.description && <p className="mt-5 max-w-4xl text-sm leading-relaxed text-ink/65">{job.description}</p>}
                                <div className="mt-5 grid gap-4 border-t border-ink/10 pt-5 text-sm text-ink/60 sm:grid-cols-3">
                                    <p><span className="text-ink/40">Sector:</span> {job.sector || job.category || "—"}</p>
                                    <p><span className="text-ink/40">Salary:</span> {job.salary_text || "—"}</p>
                                    <p><span className="text-ink/40">Deadline:</span> {job.deadline || "—"}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
